import { observer } from 'mobx-react-lite';
import { getSnapshot } from 'mobx-state-tree';
import * as React from 'react';
import { RenderElementProps } from 'slate-react';
import styled from 'styled-components';

import { RequiredAuthFake } from '../../../../../Auth/RequiredAuthFake';
import { Button } from '../../../../../ButtonV2/ButtonV2';
import { colorsV2 } from '../../../../../colors-v2';
import { Box } from '../../../../../Common/BoxV2';
import { Typography } from '../../../../../DesignSystemV2/Typography';
import { useBlankV2DoTestStore } from '../../../../plugins-common/BlankV2/store/do-test.store';
import { useExplanationsStore } from '../../../../plugins-common/Explanations/store/explanations.store';
import { useDoExerciseProgressContext } from '../../../../shared-components/Exercise/DoExerciseProgressContext';
import { useExerciseProgress } from '../../../../shared-components/Exercise/useSaveExcerciseProgress';
import { useExerciseGapFillWithHintDoTestStore } from '../../store/exercise-gap-fill-with-hint-do-test.store';
import { useRootElement } from '../../../../../../slatePlugins/common/hocs/current-element.store';

/* stylelint-disable */
const Main = styled.div`
  &.do-test {
    display: grid;
    justify-content: flex-end;
    margin: 16px 0 0;
  }

  &.result {
    margin-top: 32px;

    .result-container {
      display: grid;
      grid-template-columns: minmax(0, 1fr) max-content;
      align-items: center;
      padding: 20px 24px;

      .result-info {
        display: grid;
        gap: 4px;
      }
    }
  }
`;

export const DoTestActions = observer((props: RenderElementProps) => {
  const store = useExerciseGapFillWithHintDoTestStore();
  const blankStore = useBlankV2DoTestStore();
  const explanStore = useExplanationsStore();

  const save = useExerciseProgress();
  const { pageId } = useDoExerciseProgressContext();

  const retry = () => {
    store.retry();
    blankStore.retry();
    explanStore.retry();
  };

  const check = () => {
    store.setUserAnswers(getSnapshot(blankStore.userAnswers));
    store.check();
    store.viewResult();
    blankStore.viewResult();
    explanStore.viewResult();

    save.mutate({ ...store.preparePayload(), pageId });
  };

  return store.isViewResult ? (
    <ResultAction retry={retry} lastResult={store.resultSummary} {...props} />
  ) : (
    <DoTestAction check={check} {...props} />
  );
});

const DoTestAction = ({ check, element }) => {
  const gapFillExercise = useRootElement();

  return (
    <Main contentEditable={false} onMouseDown={(e) => e.preventDefault()} className="do-test">
      <RequiredAuthFake
        title="Chức năng này chỉ dành riêng cho thành viên của dolacademy.vn"
        onLoginSuccess={check}
      >
        {({ forceLoginToProceedAction }) => (
          <Button.Primary
            onClick={(e) => {
              e.preventDefault();
              if (gapFillExercise.isLoginRequired) {
                forceLoginToProceedAction(check);
                return;
              }
              check();
            }}
          >
            <Typography.Text variant="semi-bold/16">Check đáp án</Typography.Text>
          </Button.Primary>
        )}
      </RequiredAuthFake>
    </Main>
  );
};

interface ResultActionProps {
  retry: () => void;
  lastResult: {
    correct: number;
    total: number;
  };
}

const getResultInfo = (lastResult) => {
  const correct = lastResult?.correct || 0;
  const total = lastResult?.total || 0;
  const correctPercentage = (correct / Math.max(total, 1)) * 100;
  if (correctPercentage < 33) {
    return {
      title: 'Rất tiếc',
      background: colorsV2.primary100,
      description: `Bạn chỉ làm đúng ${correct}/${total} 😭`,
    };
  }
  if (correctPercentage <= 66) {
    return {
      title: 'Hoàn thành !',
      background: colorsV2.yellow100,
      description: `Bạn đã làm đúng được ${correct}/${total} 😚`,
    };
  }
  return {
    title: 'Chúc mừng',
    background: colorsV2.green100,
    description: `Bạn đã làm đúng được ${correct}/${total} 😍`,
  };
};

const ResultAction = ({ retry, lastResult }: ResultActionProps) => {
  const { title, background, description } = React.useMemo(
    () => getResultInfo(lastResult),
    [lastResult]
  );
  return (
    <Main contentEditable={false} onMouseDown={(e) => e.preventDefault()} className="result">
      <Box className="result-container" bg={background as any}>
        <div className="result-info">
          <Typography.Paragraph color="white100" variant="semi-bold/20">
            {title}
          </Typography.Paragraph>
          <Typography.Paragraph color="white100" variant="regular/14">
            {description}
          </Typography.Paragraph>
        </div>
        <div className="result-action">
          <Button.Primary
            style={{ minWidth: 104, backgroundColor: colorsV2.white100, color: background }}
            onClick={retry}
          >
            <Typography.Text variant="semi-bold/16">Làm lại</Typography.Text>
          </Button.Primary>
        </div>
      </Box>
    </Main>
  );
};
